import React, { useState, useEffect, useMemo, useRef } from 'react';
import DropdownNoImage from '../../../../Components/DropDown/DropdownNoImage';
import PrimaryButton from '../../../../Components/Buttons/PrimaryButton';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import MultiSelectDropdown from '../../../../Components/CustomerDetailsModal/MultiSelectDropdown';
import CustomCheckboxType2 from '../../../../Components/Checkbox/CustomCheckboxType2';
import DatePicker from 'react-datepicker';
import { getLeaveStatuses, getTherapistList, getCustomerDetails } from '../../../../APICalling/APICalling';
import { getMonthToDateRange } from '../../../../Utils/ReportDateUtils';
import styles from './Topbar.module.css';

const PLU_MAIN_CATEGORY_OPTIONS = [
    { value: 'All', label: 'All' },
    { value: 'Service', label: 'Service' },
    { value: 'Product', label: 'Product' },
    { value: 'Add-On', label: 'Add-On' },
];

const Topbar = ({ onGenerate, activeReport, onDateChange }) => {
    const {
        userAssignedOutlets,
        selectedOutlet,
        setSelectedOutlet,
        setUserAssignedOutlets,
        loggedOutletID,
        setLoggedOutletID,
        loggedUser,
        globalTherapistList,
        setGlobalTherapistList,
    } = useGlobalContext();

    const [towerOptions, setTowerOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [selectedDate, setSelectedDate] = useState('today');
    const [showCustomDates, setShowCustomDates] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showStartCalendar, setShowStartCalendar] = useState(false);
    const [showEndCalendar, setShowEndCalendar] = useState(false);
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [endDateErrorText, setEndDateErrorText] = useState('');
    const [staffError, setStaffError] = useState(false);
    const [startTimeError, setStartTimeError] = useState(false);
    const [endTimeError, setEndTimeError] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [startTimeInput, setStartTimeInput] = useState('');
    const [endTimeInput, setEndTimeInput] = useState('');
    const [staffSearchResetKey, setStaffSearchResetKey] = useState(0);
    const [includeArchivedStaff, setIncludeArchivedStaff] = useState(false);

    // Break reason/status filter (uses /status?type=break)
    const [reasons, setReasons] = useState([]);
    const [loadingReasons, setLoadingReasons] = useState(false);
    const [selectedReasons, setSelectedReasons] = useState([]);
    const [hasInitializedReasons, setHasInitializedReasons] = useState(false);
    const [reasonSearchResetKey, setReasonSearchResetKey] = useState(0);

    // PLU main category filter (single select)
    const [selectedMainCategory, setSelectedMainCategory] = useState('All');

    // Therapist/Staff data
    const [therapists, setTherapists] = useState([]);
    const [loadingTherapists, setLoadingTherapists] = useState(false);
    const [hasInitializedStaff, setHasInitializedStaff] = useState(false);
    const startTimeDropdownRef = useRef(null);
    const endTimeDropdownRef = useRef(null);
    const therapistFetchInFlightRef = useRef(null);
    const isSalesLikeReport =
        activeReport === 'sales' ||
        activeReport === 'salesSummary' ||
        activeReport === 'paymentSummary' ||
        activeReport === 'plu';
    const isTimeEnabledReport =
        activeReport === 'checkin' ||
        activeReport === 'break' ||
        activeReport === 'payroll' ||
        activeReport === 'sales' ||
        activeReport === 'salesSummary' ||
        activeReport === 'cancellation' ||
        activeReport === 'paymentSummary' ||
        activeReport === 'paymentDetails';

    const isBreakReport = activeReport === 'break';
    const isPluReport = activeReport === 'plu';

    const getYesterdayDate = () => {
        const yesterday = new Date();
        yesterday.setHours(0, 0, 0, 0);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
    };

    // Custom date range is 35 days inclusive for every report (Sep 15 -> Oct 19).
    const getCustomRangeMaxEndDate = (start) => {
        const s = new Date(start);
        const maxEnd = new Date(s);
        maxEnd.setDate(s.getDate() + 34);
        return maxEnd;
    };

    const getTherapistOutletRequest = () => {
        if (!selectedOutlet) {
            return { outlet: null, cacheKey: null, shouldFetch: false };
        }

        return { outlet: selectedOutlet, cacheKey: selectedOutlet, shouldFetch: true };
    };

    const therapistOutletCacheKey = useMemo(() => {
        if (isSalesLikeReport) return null;
        const { cacheKey, shouldFetch } = getTherapistOutletRequest();
        return shouldFetch ? cacheKey : null;
    }, [isSalesLikeReport, selectedOutlet]);

    // Helper function to format date ranges
    const getDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${startStr} - ${endStr}`;
    };

    // Get date for today
    const getToday = () => {
        const today = new Date();
        return today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get date for yesterday
    const getYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get this week range
    const getThisWeekRange = () => {
        const today = new Date();
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay() + 1)); // Monday
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 7)); // Sunday
        return getDateRange(firstDay, lastDay);
    };

    // Get last week range
    const getLastWeekRange = () => {
        const today = new Date();
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay() - 6)); // Previous Monday
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay())); // Previous Sunday
        return getDateRange(firstDay, lastDay);
    };

    // Get last two weeks range
    const getLastTwoWeeksRange = () => {
        const today = new Date();
        const endDay = new Date(today.setDate(today.getDate() - today.getDay())); // Last Sunday
        const startDay = new Date(endDay);
        startDay.setDate(endDay.getDate() - 13); // 14 days before
        return getDateRange(startDay, endDay);
    };

    // Generate monthly options for the past year
    const generateMonthlyOptions = (excludeCurrentMonth = false) => {
        const options = [];
        const today = new Date();
        const startIndex = excludeCurrentMonth ? 1 : 0;
        
        for (let i = startIndex; i < 12 + startIndex; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const value = `month-${date.getFullYear()}-${date.getMonth() + 1}`;
            options.push({ label: monthYear, value: value });
        }
        
        return options;
    };

    // Generate time options from 12:00 AM to 11:59 PM in 1-minute intervals
    const generateTimeOptions = useMemo(() => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 1) {
                const minuteStr = String(minute).padStart(2, '0');
                let hourFormatted;
                let ampm;

                if (hour === 0) {
                    hourFormatted = 12;
                    ampm = 'AM';
                } else if (hour === 12) {
                    hourFormatted = 12;
                    ampm = 'PM';
                } else if (hour < 12) {
                    hourFormatted = hour;
                    ampm = 'AM';
                } else {
                    hourFormatted = hour - 12;
                    ampm = 'PM';
                }

                options.push({
                    label: `${hourFormatted}:${minuteStr} ${ampm}`,
                    value: `${String(hour).padStart(2, '0')}:${minuteStr}`,
                });
            }
        }
        return options;
    }, []);

    // Fetch therapists
    const fetchTherapists = async (cacheKey, outlet) => {
        if (!cacheKey) return;

        try {
            setLoadingTherapists(true);

            const response = await getTherapistList({
                ...(outlet != null ? { outlet_id: outlet } : {}),
                pagination: 0,
                page: 1,
            });

            if (response?.data) {
                const allStaff = response.data?.data?.data?.list?.staffs || [];
                // Staff only (not receptionists), keeping both active and archived
                const staffOnly = allStaff.filter((staff) => {
                    const role = (staff.role || '').toString().toLowerCase();
                    const isReception = role.includes('receptionist');
                    const isStaff = role ? role.includes('staff') : true;
                    return !isReception && isStaff;
                });

                const therapistsOnly = staffOnly
                    .filter((staff) => Number(staff.status) === 1)
                    .map((staff) => ({ ...staff, isArchived: false }));
                const archivedOnly = staffOnly
                    .filter((staff) => Number(staff.status) === 0)
                    .map((staff) => ({ ...staff, isArchived: true }));

                const combinedStaff = [...therapistsOnly, ...archivedOnly];

                setTherapists(combinedStaff);
                setGlobalTherapistList({
                    outletId: cacheKey,
                    data: combinedStaff,
                    archivedData: archivedOnly,
                });

                if (combinedStaff.length > 0) {
                    setSelectedStaff(combinedStaff.map(t => t.id));
                    setHasInitializedStaff(true);
                }
            } else {
                console.error('Failed to fetch therapists:', response);
            }
        } catch (error) {
            console.error('Error fetching therapists:', error);
        } finally {
            setLoadingTherapists(false);
        }
    };

    // Date options with memoization (PLU excludes today / periods that include today or future)
    const dateOptions = useMemo(() => {
        const monthlyOptions = generateMonthlyOptions(isPluReport);

        if (isPluReport) {
            const { start: monthToDateStart, end: monthToDateEnd } = getMonthToDateRange();
            const formatShort = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const timeGap = '\u00A0\u00A0';
            return [
                {
                    label: `${formatShort(monthToDateStart)}${timeGap}12:00 To: ${formatShort(monthToDateEnd)}${timeGap}12:00`,
                    value: 'monthToDate',
                },
                { label: `Yesterday (${getYesterday()})`, value: 'yesterday' },
                { label: 'Last Week', value: 'lastWeek' },
                { label: `Last Two Weeks (${getLastTwoWeeksRange()})`, value: 'lastTwoWeeks' },
                { label: 'Custom Time Period', value: 'custom' },
                ...monthlyOptions
            ];
        }

        return [
            { label: `Today (${getToday()})`, value: 'today' },
            { label: `Yesterday (${getYesterday()})`, value: 'yesterday' },
            { label: `This Week (${getThisWeekRange()})`, value: 'thisWeek' },
            { label: 'Last Week', value: 'lastWeek' },
            { label: `Last Two Weeks (${getLastTwoWeeksRange()})`, value: 'lastTwoWeeks' },
            { label: 'Custom Time Period', value: 'custom' },
            ...monthlyOptions
        ];
    }, [isPluReport]);

    // Refresh assigned outlets via /users/:id?panel=outlet on reports landing
    useEffect(() => {
        const userId = localStorage.getItem('user_id') || loggedUser;
        if (!userId || userId === 'null') return;

        let isMounted = true;

        const fetchUserAssignedOutlets = async () => {
            try {
                const response = await getCustomerDetails(userId);
                if (!isMounted) return;
                if (response?.data?.success && response?.data?.data) {
                    const user = response.data.data;
                    localStorage.setItem('user_assigned_outlets', JSON.stringify(user.outlet_details || []));
                    setLoggedOutletID(user.outlet_id);
                    setUserAssignedOutlets(user.outlet_details || []);
                }
            } catch (error) {
                console.error('Error fetching user assigned outlets:', error);
            }
        };

        fetchUserAssignedOutlets();

        return () => {
            isMounted = false;
        };
        // Run once on reports landing; outlet dropdown rebuilds when userAssignedOutlets updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize tower options from user assigned outlets
    useEffect(() => {
        if (userAssignedOutlets?.length) {
            // Filter to only show outlets with "edit" mode
            // console.log("userAssignedOutlets-->", userAssignedOutlets);
            const editOnlyOutlets = userAssignedOutlets.filter(outlet => outlet.mode === "edit");
            
            const mapped = editOnlyOutlets.map((outlet) => ({
                label: (
                    <span className={styles.outletText}>
                        {outlet.name}
                        {outlet.mode === "edit" && " (Edit)"}
                    </span>
                ),
                value: outlet.id,
                data: outlet,
            }));

            setTowerOptions(mapped);
            
            // Check if current selected outlet has edit permission
            const currentOutletHasEdit = editOnlyOutlets.some((o) => o.id === selectedOutlet);
            
            // If no outlet selected OR current outlet doesn't have edit permission, set default
            if (!selectedOutlet || !currentOutletHasEdit) {
                // Prefer loggedOutletID if it has edit permission, otherwise use first edit outlet
                const loggedOutletHasEdit = editOnlyOutlets.some((o) => o.id === loggedOutletID);
                if (loggedOutletHasEdit) {
                    setSelectedOutlet(loggedOutletID);
                } else if (editOnlyOutlets.length > 0) {
                    setSelectedOutlet(editOnlyOutlets[0].id);
                }
            }
        }
    }, [userAssignedOutlets, loggedOutletID, setSelectedOutlet, selectedOutlet]);

    // Fetch break reasons/statuses
    useEffect(() => {
        let isMounted = true;

        const fetchReasons = async () => {
            if (!isBreakReport) {
                if (!isMounted) return;
                setReasons([]);
                setSelectedReasons([]);
                setHasInitializedReasons(false);
                setLoadingReasons(false);
                return;
            }

            try {
                setLoadingReasons(true);
                const response = await getLeaveStatuses({ type: 'break',pagination: 0, page: 1 });
                const statuses = response?.data?.data?.list?.statuses || [];

                const activeStatuses = statuses
                    .filter((s) => Number(s?.status) === 1)
                    .sort((a, b) => Number(a?.sort || 0) - Number(b?.sort || 0));

                if (!isMounted) return;
                setReasons(activeStatuses);
                setSelectedReasons([]);
                setHasInitializedReasons(false);
            } catch (error) {
                console.error('Error fetching break reasons:', error);
                if (!isMounted) return;
                setReasons([]);
                setSelectedReasons([]);
                setHasInitializedReasons(false);
            } finally {
                if (isMounted) {
                    setLoadingReasons(false);
                }
            }
        };

        fetchReasons();

        return () => {
            isMounted = false;
        };
    }, [isBreakReport]);

    // Default to "All Reasons" selected: once reasons load, select all if none chosen yet
    useEffect(() => {
        if (isBreakReport && Array.isArray(reasons) && reasons.length > 0 && !hasInitializedReasons) {
            setSelectedReasons(reasons.map((r) => r.id));
            setHasInitializedReasons(true);
        }
    }, [isBreakReport, reasons, hasInitializedReasons]);

    // Fetch therapists when outlet selection is ready
    useEffect(() => {
        if (isSalesLikeReport || !therapistOutletCacheKey) {
            return;
        }

        const hasCacheData =
            globalTherapistList?.outletId === therapistOutletCacheKey &&
            ((globalTherapistList?.data && globalTherapistList.data.length > 0) ||
                (globalTherapistList?.archivedData && globalTherapistList.archivedData.length > 0));

        if (hasCacheData) {
            setTherapists(globalTherapistList.data);
            if (globalTherapistList.data.length > 0 && !hasInitializedStaff) {
                setSelectedStaff(globalTherapistList.data.map(t => t.id));
                setHasInitializedStaff(true);
            }
            return;
        }

        if (therapistFetchInFlightRef.current === therapistOutletCacheKey) {
            return;
        }

        const { outlet } = getTherapistOutletRequest();
        therapistFetchInFlightRef.current = therapistOutletCacheKey;
        setHasInitializedStaff(false);
        setSelectedStaff([]);

        fetchTherapists(therapistOutletCacheKey, outlet).finally(() => {
            if (therapistFetchInFlightRef.current === therapistOutletCacheKey) {
                therapistFetchInFlightRef.current = null;
            }
        });
    }, [therapistOutletCacheKey, isSalesLikeReport]);

	// Default to "All Staff" selected: once therapists load, select all if none chosen yet
	useEffect(() => {
		if (therapists && therapists.length > 0 && !hasInitializedStaff) {
			setSelectedStaff(therapists.map(t => t.id));
			setHasInitializedStaff(true);
		}
	}, [therapists, hasInitializedStaff]);

	// Reset all filters (except outlet) when report changes
	useEffect(() => {
		setSelectedDate(isPluReport ? 'monthToDate' : 'today');
		setShowCustomDates(false);
		setStartDate(null);
		setEndDate(null);
		setShowStartCalendar(false);
		setShowEndCalendar(false);
		setStartDateError(false);
		setEndDateError(false);
		setEndDateErrorText('');
		setStaffError(false);
		setStartTimeError(false);
		setEndTimeError(false);
		setStartTime(null);
		setEndTime(null);
		setStartTimeInput('');
		setEndTimeInput('');
		setStaffSearchResetKey((prev) => prev + 1);
        setSelectedReasons([]);
        setHasInitializedReasons(false);
        setReasonSearchResetKey((prev) => prev + 1);
        setSelectedMainCategory('All');

		if (therapists && therapists.length > 0) {
			setSelectedStaff(therapists.map((t) => t.id));
			setHasInitializedStaff(true);
		} else {
			setSelectedStaff([]);
			setHasInitializedStaff(false);
		}
        setIncludeArchivedStaff(false);

	}, [activeReport]);

    const handleTowerSelect = (selectedOption) => {
        const newOutletId = selectedOption?.value;
        if (newOutletId !== selectedOutlet) {
            setSelectedOutlet(newOutletId);
        }
    };

    const handleStaffSelect = (values) => {
        setSelectedStaff(values);
        // Clear error when staff selection changes
        setStaffError(false);
    };

    const handleArchivedStaffToggle = () => {
        setIncludeArchivedStaff((prev) => !prev);
    };

    const handleReasonSelect = (values) => {
        setSelectedReasons(values);
    };

    const handleDateSelect = (selectedOption) => {
        const fallback = isPluReport ? 'monthToDate' : 'today';
        const value = selectedOption?.value || fallback;
        setSelectedDate(value);
        
        // Notify parent component of date change
        if (onDateChange) {
            onDateChange(value);
        }
        
        // Show custom date inputs if custom is selected
        if (value === 'custom') {
            setShowCustomDates(true);
        } else {
            setShowCustomDates(false);
            setStartDate(null);
            setEndDate(null);
            // Only reset time if it's checkin or break or payroll or sales or cancellation report
            if (isTimeEnabledReport) {
                setStartTime(null);
                setEndTime(null);
                setStartTimeInput('');
                setEndTimeInput('');
            }
            setStartDateError(false);
            setEndDateError(false);
            setStartTimeError(false);
            setEndTimeError(false);
        }
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
        setShowStartCalendar(false);
        setStartDateError(false);
        setEndDateErrorText('');
        // Reset start time when date changes
        if (!date) {
            setStartTime(null);
            setStartTimeInput('');
        }
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setShowEndCalendar(false);
        setEndDateError(false);
        setEndDateErrorText('');
        // Reset end time when date changes
        if (!date) {
            setEndTime(null);
            setEndTimeInput('');
        }
    };

    const to12HourLabel = (value) => {
        if (!value || !/^\d{2}:\d{2}$/.test(value)) return '';
        const [hour24, minute] = value.split(':').map(Number);
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        const hour12 = hour24 % 12 || 12;
        return `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
    };

    const handleStartTimeSelect = (selectedOption) => {
        const selectedValue = selectedOption?.value || null;
        setStartTime(selectedValue);
        setStartTimeInput(selectedValue ? to12HourLabel(selectedValue) : '');
        setStartTimeError(false);
    };

    const handleEndTimeSelect = (selectedOption) => {
        const selectedValue = selectedOption?.value || null;
        setEndTime(selectedValue);
        setEndTimeInput(selectedValue ? to12HourLabel(selectedValue) : '');
        setEndTimeError(false);
    };

    const formatTimeInputForTyping = (value, shouldAutoAppendMeridiem = true) => {
        const rawValue = (value || '').toUpperCase();
        const digits = rawValue.replace(/\D/g, '').slice(0, 4);
        const letters = rawValue.replace(/[^APM]/g, '');

        if (!digits) return '';

        let formatted = digits.slice(0, Math.min(2, digits.length));
        if (digits.length >= 2) {
            formatted = `${digits.slice(0, 2)}:`;
        }
        if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}:${digits.slice(2)}`;
        }

        if (digits.length === 4 && shouldAutoAppendMeridiem) {
            const meridiem = letters.startsWith('P') ? 'PM' : 'AM';
            formatted = `${formatted} ${meridiem}`;
        }

        return formatted;
    };

    const handleStartTimeInputChange = (event) => {
        const isDeleting = event?.nativeEvent?.inputType?.startsWith('delete');
        const nextValue = formatTimeInputForTyping(event.target.value, !isDeleting);
        setStartTimeInput(nextValue);
        setStartTime(null);
        const parsedTypedValue = parseInputTo24Hour(nextValue);
        setStartTimeError(Boolean(nextValue) && nextValue.includes(':') && !parsedTypedValue);
    };

    const handleEndTimeInputChange = (event) => {
        const isDeleting = event?.nativeEvent?.inputType?.startsWith('delete');
        const nextValue = formatTimeInputForTyping(event.target.value, !isDeleting);
        setEndTimeInput(nextValue);
        setEndTime(null);
        const parsedTypedValue = parseInputTo24Hour(nextValue);
        setEndTimeError(Boolean(nextValue) && nextValue.includes(':') && !parsedTypedValue);
    };

    const parseInputTo24Hour = (value) => {
        const trimmed = (value || '').trim().toLowerCase();
        if (!trimmed) return null;

        const ampmMatch = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/i);
        if (ampmMatch) {
            let hours = Number(ampmMatch[1]);
            const minutes = Number(ampmMatch[2] || '0');
            const meridiem = ampmMatch[3].toLowerCase();

            if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
            if (meridiem === 'pm' && hours !== 12) hours += 12;
            if (meridiem === 'am' && hours === 12) hours = 0;

            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        const hhmmMatch = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))$/);
        if (hhmmMatch) {
            const hours = Number(hhmmMatch[1]);
            const minutes = Number(hhmmMatch[2]);
            if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        const fourDigitsMatch = trimmed.match(/^(\d{1,2})(\d{2})$/);
        if (fourDigitsMatch) {
            const hours = Number(fourDigitsMatch[1]);
            const minutes = Number(fourDigitsMatch[2]);
            if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        return null;
    };

    const handleTimeInputBlur = (value, setter, displaySetter, errorSetter) => {
        if (!value) return;
        const parsedValue = parseInputTo24Hour(value);
        if (!parsedValue) {
            errorSetter(true);
            return;
        }
        setter(parsedValue);
        displaySetter(to12HourLabel(parsedValue));
        errorSetter(false);
    };

    const handleTimeInputKeyDown = (event, value, setter, displaySetter, errorSetter) => {
        if (event.key.length === 1 && /[a-z]/i.test(event.key)) {
            const hasMeridiem = /\s(AM|PM)$/i.test(value || '');
            if (hasMeridiem) {
                event.preventDefault();
                const toggled = value.trim().toUpperCase().endsWith('AM')
                    ? value.replace(/\sAM$/i, ' PM')
                    : value.replace(/\sPM$/i, ' AM');
                displaySetter(toggled);
                const parsed = parseInputTo24Hour(toggled);
                if (parsed) {
                    setter(parsed);
                    errorSetter(false);
                }
                return;
            }
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            handleTimeInputBlur(value, setter, displaySetter, errorSetter);
        }
    };

    const openTimeDropdown = (dropdownRef) => {
        const dropdownToggle = dropdownRef?.current?.querySelector('.dropdownToggle');
        if (dropdownToggle) {
            dropdownToggle.click();
        }
    };

    // Custom read-only input component that prevents typing but displays selected date
    const ReadOnlyDateInput = React.forwardRef(({ value, onClick, placeholder, customOnClick, hasError }, ref) => {
        const handleClick = (e) => {
            e.preventDefault();
            // Use custom click handler if provided, otherwise use default onClick
            if (customOnClick) {
                customOnClick(e);
            } else if (onClick) {
                onClick(e);
            }
        };

        const handleKeyDown = (e) => {
            // Allow Tab for navigation, Enter and Space to open calendar
            if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(e);
                }
                return;
            }
            // Prevent all other keyboard input
            e.preventDefault();
        };

        return (
            <input
                ref={ref}
                type="text"
                value={value || ''}
                onClick={handleClick}
                onChange={() => {}} // Prevent direct text changes
                onKeyDown={handleKeyDown}
                onPaste={(e) => e.preventDefault()}
                placeholder={placeholder}
                readOnly
                className={`${styles.dateInput} ${hasError ? styles.dateInputError : ''}`}
                style={{ cursor: 'pointer' }}
            />
        );
    });
    ReadOnlyDateInput.displayName = 'ReadOnlyDateInput';

    function buildParams(reportId = activeReport, customStartTime = startTime, customEndTime = endTime) {
        const params = {
            date: selectedDate,
            outlet: selectedOutlet,
        };

        if (reportId !== 'sales' && reportId !== 'salesSummary' && reportId !== 'paymentSummary' && reportId !== 'plu') {
            const activeSelected = Array.isArray(selectedStaff) ? selectedStaff : [];
            const hasAnySelection = activeSelected.length > 0;

            if (!hasAnySelection) {
                params.staff = [];
            } else if (activeSelected.length < therapists.length) {
                params.staff = activeSelected;
            }

            if (reportId !== 'paymentDetails') {
                params.archive = 1;
            }
        }

        if (reportId === 'break') {
            const activeSelected = Array.isArray(selectedReasons) ? selectedReasons : [];
            const hasAnySelection = activeSelected.length > 0;

            if (!hasAnySelection) {
                params.status = [];
            } else if (Array.isArray(reasons) && reasons.length > 0 && activeSelected.length < reasons.length) {
                params.status = activeSelected;
            }
        }

        if (reportId === 'plu') {
            params.main_category = selectedMainCategory || 'All';
        }

        if (selectedDate === 'custom' && startDate && endDate) {
            const formatDate = (date, time = null) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;

                if ((reportId === 'checkin' || reportId === 'break' || reportId === 'payroll' || reportId === 'sales' || reportId === 'salesSummary' || reportId === 'cancellation' || reportId === 'paymentSummary' || reportId === 'paymentDetails') && time) {
                    const timeValue = typeof time === 'object' ? time.value : time;
                    if (timeValue) {
                        return `${dateStr} ${timeValue}`;
                    }
                }
                return dateStr;
            };
            params.startDate = formatDate(startDate, (reportId === 'checkin' || reportId === 'break' || reportId === 'payroll' || reportId === 'sales' || reportId === 'salesSummary' || reportId === 'cancellation' || reportId === 'paymentSummary' || reportId === 'paymentDetails') ? customStartTime : null);
            params.endDate = formatDate(endDate, (reportId === 'checkin' || reportId === 'break' || reportId === 'payroll' || reportId === 'sales' || reportId === 'salesSummary' || reportId === 'cancellation' || reportId === 'paymentSummary' || reportId === 'paymentDetails') ? customEndTime : null);
        }

        return params;
    }

    const handleMainCategorySelect = (option) => {
        setSelectedMainCategory(option?.value || 'All');
    };

    const handleGenerate = () => {
        setStaffSearchResetKey((prev) => prev + 1);
        // Validate custom date selection
        let finalStartTime = startTime;
        let finalEndTime = endTime;

        if (selectedDate === 'custom') {
            const parsedStartTime = startTime || parseInputTo24Hour(startTimeInput);
            const parsedEndTime = endTime || parseInputTo24Hour(endTimeInput);
            finalStartTime = parsedStartTime || null;
            finalEndTime = parsedEndTime || null;
            if (parsedStartTime && parsedStartTime !== startTime) {
                setStartTime(parsedStartTime);
                setStartTimeInput(to12HourLabel(parsedStartTime));
            }
            if (parsedEndTime && parsedEndTime !== endTime) {
                setEndTime(parsedEndTime);
                setEndTimeInput(to12HourLabel(parsedEndTime));
            }

            const isStartEmpty = !startDate;
            const isEndEmpty = !endDate;
            const needsTime =
                activeReport === 'checkin' ||
                activeReport === 'break' ||
                activeReport === 'payroll' ||
                activeReport === 'sales' ||
                activeReport === 'salesSummary' ||
                activeReport === 'cancellation' ||
                activeReport === 'paymentSummary' ||
                activeReport === 'paymentDetails';
            const isStartTimeEmpty = needsTime && !parsedStartTime;
            const isEndTimeEmpty = needsTime && !parsedEndTime;

            if (isStartEmpty || isEndEmpty) {
                setStartDateError(isStartEmpty);
                setEndDateError(isEndEmpty);
                setStartTimeError(isStartTimeEmpty);
                setEndTimeError(isEndTimeEmpty);
                return;
            }

            if (isPluReport) {
                const yesterday = getYesterdayDate();
                const startTooRecent = startDate && startDate > yesterday;
                const endTooRecent = endDate && endDate > yesterday;
                if (startTooRecent || endTooRecent) {
                    setStartDateError(!!startTooRecent);
                    setEndDateError(!!endTooRecent);
                    return;
                }
            }

            if (endDate > getCustomRangeMaxEndDate(startDate)) {
                setEndDateErrorText('Date range cannot exceed 35 days');
                setEndDateError(true);
                return;
            }

            if (isStartTimeEmpty || isEndTimeEmpty) {
                setStartTimeError(isStartTimeEmpty);
                setEndTimeError(isEndTimeEmpty);
                return;
            }
        }
        
        setStartDateError(false);
        setEndDateError(false);
        setStaffError(false);
        setStartTimeError(false);
        setEndTimeError(false);
        if (onGenerate) {
            onGenerate(buildParams(activeReport, finalStartTime, finalEndTime));
        }
    };

    return (
        <div className={styles.topbar}>
            <div className={styles.topbarContent}>
                <div className={styles.filtersSection}>
                    <DropdownNoImage
                        options={towerOptions}
                        selectedLabel={
                            towerOptions.find(
                                (tower) => tower.value === selectedOutlet
                            )?.label || ""
                        }
                        onSelect={handleTowerSelect}
                        dropdownClass={`${styles.dropdown} topbarDropdown`}
                        dropdownMenuClass={`${styles.dropdownMenu} topbarDropdownMenu`}
                        placeholder="List Towers"
                        placeholderClass={styles.placeholder}
                        labelText=""
                    />

                    {/* Hide staff filter for sales and check-in reports */}
                    {!isSalesLikeReport && activeReport !== 'checkin' && (
                        <div className={styles.staffDropdownWrapper}>
                            <div className={`${styles.dropdown} ${staffError ? styles.dropdownError : ''}`}>
                                <MultiSelectDropdown
                                    options={Array.isArray(therapists) ? therapists.map(therapist => ({
                                        value: therapist.id,
                                        label: (therapist.alias || therapist.name || 'Unnamed') + (therapist.isArchived ? ' (Archived)' : ''),
                                        id: therapist.id,
                                        pagerNumber: therapist.pager_number,
                                        gender: therapist.gender,
                                        isStarred: therapist.alias && therapist.alias.includes('*')
                                    })) : []}
                                    selectedValues={selectedStaff}
                                    onChange={handleStaffSelect}
                                    allSelectedText="All Staff"
                                    placeholder="0 selected"
                                    searchPlaceholder="Search staff..."
                                    loading={loadingTherapists}
                                    hasMore={false}
                                    iconColor="#000"
                                    iconStrokeWidth="2"
                                    textColor="var(--neutral-900)"
                                    resetSearchTrigger={staffSearchResetKey}
                                    autoFocusSearch
                                />
                            </div>
                            {staffError && (
                                <div className={styles.staffErrorMessage}>
                                    Please select therapist
                                </div>
                            )}
                        </div>
                    )}

                    {/* Break report reason filter */}
                    {!isSalesLikeReport && isBreakReport && (
                        <div className={styles.reasonDropdownWrapper}>
                            <div className={styles.dropdown}>
                                <MultiSelectDropdown
                                    options={Array.isArray(reasons) ? reasons.map((reason) => ({
                                        value: reason.id,
                                        label: reason.name || 'Unnamed',
                                        id: reason.id,
                                    })) : []}
                                    selectedValues={selectedReasons}
                                    onChange={handleReasonSelect}
                                    allSelectedText="All Reasons"
                                    placeholder="0 selected"
                                    searchPlaceholder="Search reasons..."
                                    loading={loadingReasons}
                                    hasMore={false}
                                    iconColor="#000"
                                    iconStrokeWidth="2"
                                    textColor="var(--neutral-900)"
                                    resetSearchTrigger={reasonSearchResetKey}
                                    showOptionAvatar={false}
                                    autoFocusSearch
                                />
                            </div>
                        </div>
                    )}
                    {/* {!isSalesLikeReport && activeReport !== 'paymentDetails' && activeReport !== 'checkin' && (
                        <div className={styles.archiveCheckboxWrapper}>
                            <CustomCheckboxType2
                                id="archive-staff-toggle"
                                checked={includeArchivedStaff}
                                label="Archive Staff"
                                handleClick={handleArchivedStaffToggle}
                            />
                        </div>
                    )} */}

                    <DropdownNoImage
                        options={dateOptions}
                        selectedLabel={
                            dateOptions.find((option) => option.value === selectedDate)?.label || ""
                        }
                        onSelect={handleDateSelect}
                        dropdownClass={`${styles.dropdown} ${selectedDate === 'custom' ? styles.minimizedDropdown : ''} topbarDropdown`}
                        dropdownMenuClass={`${styles.dropdownMenu} topbarDropdownMenu`}
                        placeholder="Select Date"
                        placeholderClass={styles.placeholder}
                        labelText=""
                    />

                    {showCustomDates && (
                        <>
                            <div className={styles.dateInputWrapper}>
                                <DatePicker
                                    locale="en"
                                    selected={startDate}
                                    onChange={handleStartDateChange}
                                    onSelect={handleStartDateChange}
                                    dateFormat="MMM d, yyyy"
                                    open={showStartCalendar}
                                    onClickOutside={() => setShowStartCalendar(false)}
                                    calendarClassName={styles.customDatePicker}
                                    popperClassName={isBreakReport ? styles.breakReportDatePopper : ''}
                                    placeholderText="Start date"
                                    maxDate={
                                        isPluReport
                                            ? (endDate && endDate < getYesterdayDate()
                                                ? endDate
                                                : getYesterdayDate())
                                            : endDate
                                    }
                                    customInput={
                                        <ReadOnlyDateInput 
                                            customOnClick={() => setShowStartCalendar(!showStartCalendar)}
                                            hasError={startDateError}
                                        />
                                    }
                                />
                                {startDateError && (
                                    <div className={styles.dateErrorMessage}>
                                        Please fill Start date
                                    </div>
                                )}
                                {startDate && isTimeEnabledReport && (
                                    <div className={styles.timeDropdownWrapper} style={{ position: 'relative' }} ref={startTimeDropdownRef}>
                                        <DropdownNoImage
                                            options={generateTimeOptions}
                                            selectedLabel={
                                                startTime
                                                    ? generateTimeOptions.find((opt) => opt.value === startTime)?.label || ''
                                                    : ''
                                            }
                                            onSelect={handleStartTimeSelect}
                                            dropdownClass={`${styles.hiddenTimeDropdown} ${styles.timeDropdown} topbarDropdown`}
                                            dropdownMenuClass={`${styles.dropdownMenu} ${styles.timeDropdownMenu} topbarDropdownMenu`}
                                            placeholder="Select time"
                                            placeholderClass={styles.placeholder}
                                            labelText=""
                                            enableSearch
                                            searchPlaceholder="Search time..."
                                        />
                                        <div
                                            className={`${styles.timeInputWrapper} ${startTimeError ? styles.dateInputError : ''}`}
                                        >
                                            <input
                                                type="text"
                                                value={startTimeInput}
                                                onChange={handleStartTimeInputChange}
                                                onKeyDown={(event) =>
                                                    handleTimeInputKeyDown(
                                                        event,
                                                        startTimeInput,
                                                        setStartTime,
                                                        setStartTimeInput,
                                                        setStartTimeError
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleTimeInputBlur(
                                                        startTimeInput,
                                                        setStartTime,
                                                        setStartTimeInput,
                                                        setStartTimeError
                                                    )
                                                }
                                                className={styles.timeInput}
                                                placeholder="--:--"
                                            />
                                            <button
                                                type="button"
                                                className={styles.timeInputClockIcon}
                                                aria-label="Open start time options"
                                                onClick={() => openTimeDropdown(startTimeDropdownRef)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </button>
                                        </div>
                                        {startTimeError && (
                                            <div className={styles.dateErrorMessage}>
                                                Please fill correct Start time
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className={styles.dateInputWrapper}>
                                <DatePicker
                                    locale="en"
                                    selected={endDate}
                                    onChange={handleEndDateChange}
                                    onSelect={handleEndDateChange}
                                    dateFormat="MMM d, yyyy"
                                    open={showEndCalendar}
                                    onClickOutside={() => setShowEndCalendar(false)}
                                    calendarClassName={styles.customDatePicker}
                                    popperClassName={isBreakReport ? styles.breakReportDatePopper : ''}
                                    placeholderText="End date"
                                    minDate={startDate}
                                    maxDate={(() => {
                                        if (!isPluReport) {
                                            return startDate ? getCustomRangeMaxEndDate(startDate) : null;
                                        }
                                        const yesterday = getYesterdayDate();
                                        if (!startDate) return yesterday;
                                        const maxFromStart = getCustomRangeMaxEndDate(startDate);
                                        return maxFromStart < yesterday ? maxFromStart : yesterday;
                                    })()}
                                    customInput={
                                        <ReadOnlyDateInput 
                                            customOnClick={() => setShowEndCalendar(!showEndCalendar)}
                                            hasError={endDateError}
                                        />
                                    }
                                />
                                {endDateError && (
                                    <div className={styles.dateErrorMessage}>
                                        {endDateErrorText || 'Please fill End date'}
                                    </div>
                                )}
                                {endDate && isTimeEnabledReport && (
                                    <div className={styles.timeDropdownWrapper} style={{ position: 'relative' }} ref={endTimeDropdownRef}>
                                        <DropdownNoImage
                                            options={generateTimeOptions}
                                            selectedLabel={
                                                endTime
                                                    ? generateTimeOptions.find((opt) => opt.value === endTime)?.label || ''
                                                    : ''
                                            }
                                            onSelect={handleEndTimeSelect}
                                            dropdownClass={`${styles.hiddenTimeDropdown} ${styles.timeDropdown} topbarDropdown`}
                                            dropdownMenuClass={`${styles.dropdownMenu} ${styles.timeDropdownMenu} topbarDropdownMenu`}
                                            placeholder="Select time"
                                            placeholderClass={styles.placeholder}
                                            labelText=""
                                            enableSearch
                                            searchPlaceholder="Search time..."
                                        />
                                        <div
                                            className={`${styles.timeInputWrapper} ${endTimeError ? styles.dateInputError : ''}`}
                                        >
                                            <input
                                                type="text"
                                                value={endTimeInput}
                                                onChange={handleEndTimeInputChange}
                                                onKeyDown={(event) =>
                                                    handleTimeInputKeyDown(
                                                        event,
                                                        endTimeInput,
                                                        setEndTime,
                                                        setEndTimeInput,
                                                        setEndTimeError
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleTimeInputBlur(
                                                        endTimeInput,
                                                        setEndTime,
                                                        setEndTimeInput,
                                                        setEndTimeError
                                                    )
                                                }
                                                className={styles.timeInput}
                                                placeholder="--:--"
                                            />
                                            <button
                                                type="button"
                                                className={styles.timeInputClockIcon}
                                                aria-label="Open end time options"
                                                onClick={() => openTimeDropdown(endTimeDropdownRef)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </button>
                                        </div>
                                        {endTimeError && (
                                            <div className={styles.dateErrorMessage}>
                                                Please fill correct End time
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {isPluReport && (
                        <DropdownNoImage
                            options={PLU_MAIN_CATEGORY_OPTIONS}
                            selectedLabel={
                                PLU_MAIN_CATEGORY_OPTIONS.find(
                                    (option) => option.value === selectedMainCategory
                                )?.label || 'All'
                            }
                            onSelect={handleMainCategorySelect}
                            dropdownClass={`${styles.dropdown} topbarDropdown`}
                            dropdownMenuClass={`${styles.dropdownMenu} topbarDropdownMenu`}
                            placeholder="Select Category"
                            placeholderClass={styles.placeholder}
                            labelText=""
                        />
                    )}
                </div>
                
                <div className={styles.actionSection}>
                    <PrimaryButton
                        text="Generate"
                        onClick={handleGenerate}
                        className={styles.generateButton}
                    />
                </div>
            </div>
        </div>
    );
};

export default Topbar;

